import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { apiFetch } from '../../lib/api';
import { dateOnly, formatDate } from '../../lib/dateFormat';

const initialForm = {
  scheduled_class_id: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  name_suffix: '',
  birth_date: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: 'TX',
  zip: '',
  driver_number: '',
  ssn_last4: '',
  probation_county: '',
  probation_attorney: '',
  referring_county: '',
  hear_about_us: '',
  notes: ''
};

function formatTime(value) {
  if (!value) return '';
  const [hourStr, minute] = String(value).slice(0, 5).split(':');
  const hour = Number(hourStr);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
}

function classLabel(row) {
  const dateRange = row.end_date && dateOnly(row.end_date) !== dateOnly(row.class_date)
    ? `${formatDate(row.class_date)} - ${formatDate(row.end_date)}`
    : formatDate(row.class_date);
  return [
    row.class_name,
    row.class_code ? `(${row.class_code})` : '',
    dateRange,
    formatTime(row.class_time),
    row.location ? `— ${row.location}` : ''
  ].filter(Boolean).join(' ');
}

export default function PublicEnrollmentPage() {
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const selectedClass = useMemo(
    () => classes.find((row) => row.id === form.scheduled_class_id),
    [classes, form.scheduled_class_id]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setForm((current) => ({
      ...current,
      payment_reference: params.get('payment_id') || params.get('reference') || '',
      payment_amount: params.get('amount') || ''
    }));

    async function loadClasses() {
      setLoading(true);
      setError('');
      try {
        const response = await apiFetch('/api/public/future-classes', { method: 'GET' });
        if (!response.ok) throw new Error(response.data?.error || 'Unable to load available classes');
        setClasses(response.data?.classes || []);
      } catch (err) {
        setError(err.message || 'Unable to load available classes');
      } finally {
        setLoading(false);
      }
    }

    loadClasses();
  }, []);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(null);

    try {
      const response = await apiFetch('/api/public/enroll', {
        method: 'POST',
        body: JSON.stringify(form)
      });

      if (!response.ok) throw new Error(response.data?.error || 'Unable to submit registration');

      setSuccess(response.data || { message: 'Registration received.' });
      setForm(initialForm);
    } catch (err) {
      setError(err.message || 'Unable to submit registration');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #07111f 0%, #10233d 45%, #172b4d 100%)', py: 5 }}>
      <Container maxWidth="md">
        <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}>
          <Box sx={{ p: { xs: 3, md: 5 }, background: 'linear-gradient(135deg, #f8fafc, #e8eef8)' }}>
            <Chip label="Secure Registration" color="primary" sx={{ mb: 2 }} />
            <Typography variant="h3" fontWeight={800} gutterBottom>
              Alamo City LX Class Registration
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete this form after payment to send your student information and choose an upcoming class.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {success ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Thank you — your registration was received.</Typography>
                <Typography variant="body2">
                  Alamo City LX will contact you if anything else is needed.
                </Typography>
                {success.scheduled_class && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected class: {classLabel(success.scheduled_class)}
                  </Typography>
                )}
              </Alert>
            ) : (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  <FormControl fullWidth required disabled={loading || submitting}>
                    <InputLabel id="class-select-label">Future class</InputLabel>
                    <Select
                      labelId="class-select-label"
                      label="Future class"
                      value={form.scheduled_class_id}
                      onChange={(e) => updateForm('scheduled_class_id', e.target.value)}
                    >
                      {classes.map((row) => (
                        <MenuItem key={row.id} value={row.id}>
                          {classLabel(row)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedClass && (
                    <Alert severity="info">
                      <strong>{selectedClass.class_name}</strong><br />
                      {formatDate(selectedClass.class_date)} {formatTime(selectedClass.class_time)}
                      {selectedClass.location ? ` at ${selectedClass.location}` : ''}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth required label="First name" value={form.first_name} onChange={(e) => updateForm('first_name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Middle name" value={form.middle_name} onChange={(e) => updateForm('middle_name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth required label="Last name" value={form.last_name} onChange={(e) => updateForm('last_name', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel id="suffix-select-label">Suffix</InputLabel>
                        <Select
                          labelId="suffix-select-label"
                          label="Suffix"
                          value={form.name_suffix}
                          onChange={(e) => updateForm('name_suffix', e.target.value)}
                        >
                          <MenuItem value=""><em>None</em></MenuItem>
                          <MenuItem value="Jr.">Jr.</MenuItem>
                          <MenuItem value="Sr.">Sr.</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth required label="Date of birth" type="date" InputLabelProps={{ shrink: true }} value={form.birth_date} onChange={(e) => updateForm('birth_date', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth required label="Phone" value={form.phone} onChange={(e) => updateForm('phone', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth required label="Email" type="email" value={form.email} onChange={(e) => updateForm('email', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Address" value={form.address} onChange={(e) => updateForm('address', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField fullWidth label="City" value={form.city} onChange={(e) => updateForm('city', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="State" value={form.state} onChange={(e) => updateForm('state', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="ZIP" value={form.zip} onChange={(e) => updateForm('zip', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Driver license number" value={form.driver_number} onChange={(e) => updateForm('driver_number', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Last 4 of Social Security number"
                        value={form.ssn_last4}
                        onChange={(e) => updateForm('ssn_last4', e.target.value.replace(/\D/g, '').slice(-4))}
                        inputProps={{ inputMode: 'numeric', maxLength: 4 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Probation county" value={form.probation_county} onChange={(e) => updateForm('probation_county', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Probation attorney" value={form.probation_attorney} onChange={(e) => updateForm('probation_attorney', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField fullWidth label="Referring county" value={form.referring_county} onChange={(e) => updateForm('referring_county', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="Payment reference" value={form.payment_reference || ''} onChange={(e) => updateForm('payment_reference', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField fullWidth label="How did you hear about us?" value={form.hear_about_us || ''} onChange={(e) => updateForm('hear_about_us', e.target.value)} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline minRows={3} label="Notes / special instructions" value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} />
                    </Grid>
                  </Grid>

                  <Button type="submit" variant="contained" size="large" disabled={loading || submitting || !classes.length}>
                    {submitting ? 'Submitting...' : 'Submit Registration'}
                  </Button>

                  {!loading && !classes.length && (
                    <Alert severity="warning">
                      No future classes are currently available. Please contact Alamo City LX for assistance.
                    </Alert>
                  )}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
